import classnames from "classnames";
import { isNotEmpty, isPresent, noop } from "neetocist";
import { joinHyphenCase } from "neetocommons/utils/general";
import { Typography, Button, Spinner, Tooltip } from "neetoui";
import PropTypes from "prop-types";
import { isNotNil } from "ramda";
import { useTranslation } from "react-i18next";

import Dropdown from "./Dropdown";
import Status from "./Status";

const IntegrationCard = ({
  icon: Icon,
  subIcons = [],
  label = "",
  description = "",
  className = "",
  onConnect = noop,
  onDisconnect = noop,
  onManage = undefined,
  isLoading = false,
  isConnected = false,
  isFailed = false,
  isExpired = false,
  disabled = false,
  hidden = false,
  hideDisconnect = false,
  onClick: onCardClick = undefined,
  tooltipProps = {},
  customDropdown = null,
  dataTestid = "",
  connectPath = "",
  connectUrl = "",
  children,
  ...otherProps
}) => {
  const { t } = useTranslation();

  if (hidden) return null;

  const isCardClickable = isConnected && isNotNil(onCardClick);

  const cardProps = isCardClickable ? { onClick: onCardClick } : {};

  return (
    <Tooltip
      disabled={!disabled}
      position="top"
      content={
        disabled &&
        t("neetoMolecules.integrationCard.tooltip.integrationNotAvailable")
      }
      {...tooltipProps}
    >
      <div
        data-testid={isPresent(dataTestid) ? dataTestid : "integration-card"}
        className={classnames(
          "neeto-ui-border-gray-300 neeto-ui-shadow-xs hover:neeto-ui-border-gray-500 focus:neeto-ui-border-gray-500 neeto-ui-rounded-lg flex flex-col border p-6 no-underline outline-none transition-colors",
          {
            "hover:neeto-ui-bg-100 cursor-not-allowed opacity-50": disabled,
            "cursor-pointer": isCardClickable,
          },
          className
        )}
        {...cardProps}
        {...otherProps}
      >
        <div
          className="flex flex-grow justify-between"
          data-testid="integration-tabs"
        >
          <div className="flex min-w-0 flex-col justify-end">
            <div className="relative mb-4 flex h-8">
              {Icon && <Icon size={32} />}
            </div>
            <div className="my-auto">
              <Typography
                className="mb-2"
                data-testid="integration-card-label"
                style="h4"
                weight="semibold"
              >
                {label}
              </Typography>
              <Typography
                className="break-words"
                data-testid={`${joinHyphenCase(label)}-integration-description`}
                style="body2"
              >
                {description}
              </Typography>
              {children}
            </div>
          </div>
          {isNotEmpty(subIcons) && (
            <div className="flex h-24 flex-col gap-2">
              {subIcons.map((Icon, index) => (
                <Icon
                  className="neeto-ui-text-gray-600"
                  key={index}
                  size={24}
                />
              ))}
            </div>
          )}
        </div>
        <div className="neeto-ui-border-gray-300 mt-4 w-full border-t pt-2">
          {isLoading ? (
            <div className="mt-3 flex items-center justify-start">
              <Spinner />
            </div>
          ) : (
            <div className="flex h-8 items-center justify-between">
              {isConnected && <Status {...{ isExpired }} />}
              {isFailed && (
                <Button
                  className="neeto-ui-text-warning-500"
                  data-testid="connect-button"
                  disabled={disabled || isLoading}
                  href={connectUrl}
                  label={t("neetoMolecules.integrationCard.tryAgain")}
                  style="link"
                  to={connectPath}
                  onClick={onConnect}
                />
              )}
              {!isConnected && !isFailed && (
                <Button
                  data-testid="connect-button"
                  disabled={disabled || isLoading}
                  href={connectUrl}
                  label={t("neetoMolecules.integrationCard.connect")}
                  style="link"
                  to={connectPath}
                  onClick={onConnect}
                />
              )}
              {(isConnected || isFailed) && (
                <Dropdown
                  {...{ customDropdown, hideDisconnect, onDisconnect }}
                  handleManage={onManage}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </Tooltip>
  );
};

IntegrationCard.propTypes = {
  /**
   * The icon component to be displayed on the card.
   */
  icon: PropTypes.elementType,
  /**
   * Array of sub-icons to be displayed on the card.
   */
  subIcons: PropTypes.arrayOf(PropTypes.elementType),
  /**
   * The label to be displayed on the card.
   */
  label: PropTypes.string,
  /**
   * The description of the card.
   */
  description: PropTypes.string,
  /**
   * Additional class names for styling the card.
   */
  className: PropTypes.string,
  /**
   * Handler function called when the integration is connected.
   */
  onConnect: PropTypes.func,
  /**
   * Handler function called when the manage dropdown is clicked.
   */
  onManage: PropTypes.func,
  /**
   * Handler function called when clicked on integration card.
   */
  onClick: PropTypes.func,
  /**
   * Handler function called when the disconnect dropdown is clicked.
   */
  onDisconnect: PropTypes.func,
  /**
   * Flag indicating if the integration is currently loading.
   */
  isLoading: PropTypes.bool,
  /**
   * Flag indicating if the integration is connected.
   */
  isConnected: PropTypes.bool,
  /**
   * Flag indicating if the integration has failed.
   */
  isFailed: PropTypes.bool,
  /**
   * Flag indicating if the integration has expired.
   */
  isExpired: PropTypes.bool,
  /**
   * Flag indicating if the integration is disabled.
   */
  disabled: PropTypes.bool,
  /**
   * Flag indicating if the card should be hidden.
   */
  hidden: PropTypes.bool,
  /**
   * Flag indicating if the disconnect button should be hidden.
   */
  hideDisconnect: PropTypes.bool,
  /**
   * Additional props for customizing the tooltip wrapper.
   */
  tooltipProps: PropTypes.object,
  /**
   * Custom dropdown component to be rendered.
   */
  customDropdown: PropTypes.element,
  /**
   * Additional children elements to be rendered within the card.
   */
  children: PropTypes.node,
  /**
   * Internal path to the integration connection page.
   */
  connectPath: PropTypes.string,
  /**
   * External path to the integration connection page.
   */
  connectUrl: PropTypes.string,
};

export default IntegrationCard;
